import { Link } from 'react-router-dom';
import { LegalPageLayout } from './LegalPageLayout';

const EFFECTIVE_DATE = 'March 1, 2026';
const VERSION = '1.0';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout>
      <h1>Privacy Policy</h1>
      <p><strong>No Doubles Golf Co.</strong></p>
      <p><strong>Version:</strong> {VERSION}</p>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p><strong>Last Updated:</strong> {EFFECTIVE_DATE}</p>
      <p>
        No Doubles Golf Co. ("No Doubles," "we," "us," or "our") operates the No Doubles mobile
        applications and website (collectively, the "Service"). This Privacy Policy describes how
        we collect, use, store, share, and protect your personal information when you use the
        Service.
      </p>
      <p>We process your information on the following legal bases, depending on the data type and context:</p>
      <ul>
        <li><strong>Performance of contract</strong> — Processing necessary to provide the Service you signed up for (account management, swing analysis, drill delivery, subscription management).</li>
        <li><strong>Consent</strong> — Processing that requires your specific, informed consent (biometric data from video analysis, parental consent for minors).</li>
        <li><strong>Legitimate interest</strong> — Processing for product improvement, security, and fraud prevention, where our interests do not override your privacy rights.</li>
        <li><strong>Legal obligation</strong> — Processing required to comply with applicable laws (tax records, breach notification, law enforcement requests).</li>
      </ul>
      <p>
        If you do not agree with the practices described in this Privacy Policy, do not use the Service.
      </p>

      <hr />

      <h2>1. Information We Collect</h2>
      <h3>1.1 Information You Provide</h3>
      <table>
        <thead><tr><th>Data Type</th><th>Examples</th><th>Purpose</th></tr></thead>
        <tbody>
          <tr><td><strong>Account Information</strong></td><td>Name, email address, password, nickname</td><td>Account creation and authentication</td></tr>
          <tr><td><strong>Profile Information</strong></td><td>Handicap, golf goals, experience level</td><td>Personalizing your practice experience</td></tr>
          <tr><td><strong>Age/Date of Birth</strong></td><td>Date of birth or age bracket</td><td>Age verification, minor consent compliance</td></tr>
          <tr><td><strong>Parent/Guardian Information</strong></td><td>Parent name, parent email (for users 13–17)</td><td>Parental consent verification</td></tr>
          <tr><td><strong>Payment Information</strong></td><td>Processed by Apple, Google, or Stripe</td><td>Subscription billing</td></tr>
          <tr><td><strong>User Content</strong></td><td>Golf swing videos, photos, text posts</td><td>Providing analysis and feedback</td></tr>
          <tr><td><strong>Communications</strong></td><td>Support requests, feedback, emails</td><td>Responding to your inquiries</td></tr>
        </tbody>
      </table>

      <h3>1.2 Information Collected Automatically</h3>
      <table>
        <thead><tr><th>Data Type</th><th>Examples</th><th>Purpose</th></tr></thead>
        <tbody>
          <tr><td><strong>Device Information</strong></td><td>Device type, OS, app version, device identifiers</td><td>Service delivery, troubleshooting</td></tr>
          <tr><td><strong>Usage Data</strong></td><td>Features used, screens viewed, session duration</td><td>Product improvement and analytics</td></tr>
          <tr><td><strong>Log Data</strong></td><td>IP address, access times, pages viewed</td><td>Security, debugging, reliability</td></tr>
        </tbody>
      </table>

      <h3>1.3 Information Derived from Your Content</h3>
      <table>
        <thead><tr><th>Data Type</th><th>How It's Generated</th><th>Purpose</th></tr></thead>
        <tbody>
          <tr><td><strong>Movement Data</strong></td><td>Body position and joint angle estimates from video analysis</td><td>Generating swing analysis and coaching feedback</td></tr>
          <tr><td><strong>Performance Metrics</strong></td><td>Swing characteristics, drill completion, progress data</td><td>Tracking progress and adjusting practice plans</td></tr>
          <tr><td><strong>Biometric Data</strong></td><td>See Section 3</td><td>See Section 3</td></tr>
        </tbody>
      </table>

      <hr />

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li><strong>Providing the Service</strong> — Delivering swing analysis, drill recommendations, practice plans, mobility screening, and performance tracking.</li>
        <li><strong>Account Management</strong> — Creating and maintaining your account, authenticating your identity, managing your subscription.</li>
        <li><strong>Communication</strong> — Sending service-related notifications, responding to support requests.</li>
        <li><strong>Product Improvement</strong> — Analyzing usage patterns to improve features. Only anonymized, aggregated data is used.</li>
        <li><strong>Safety and Security</strong> — Detecting and preventing fraud, abuse, and unauthorized access.</li>
        <li><strong>Legal Compliance</strong> — Complying with applicable laws and regulations.</li>
      </ul>
      <h3>2.1 What We Do NOT Use Your Information For</h3>
      <ul>
        <li>We do <strong>not</strong> sell your personal information to third parties.</li>
        <li>We do <strong>not</strong> use your identifiable data to train AI models.</li>
        <li>We do <strong>not</strong> use your data for behavioral advertising.</li>
        <li>We do <strong>not</strong> share your personal information with data brokers.</li>
        <li>We do <strong>not</strong> use minor users' data for advertising of any kind.</li>
      </ul>

      <hr />

      <h2 id="3-biometric-data">3. Biometric Data</h2>
      <h3>3.1 What We Collect</h3>
      <p>
        The Service uses pose detection technology to analyze video recordings you upload. This
        analysis estimates body position, joint angles, and movement patterns. Depending on your
        jurisdiction, this data may be classified as "biometric data" under applicable law.
      </p>
      <h3>3.2 Purpose</h3>
      <p>Biometric data derived from your videos is used solely to:</p>
      <ul>
        <li>Generate swing analysis and coaching feedback</li>
        <li>Track your movement patterns over time for progress monitoring</li>
        <li>Provide mobility screening observations</li>
      </ul>
      <h3>3.3 Consent</h3>
      <p>
        Before processing any video that may generate biometric data, we obtain your informed
        consent. You may withdraw your consent at any time through your account settings.
      </p>
      <h3>3.4 Retention and Destruction</h3>
      <ul>
        <li>Biometric data is retained for a maximum of <strong>3 years</strong> from collection, or until account deletion, whichever is shorter.</li>
        <li>Upon account deletion or consent withdrawal, biometric data is permanently deleted within <strong>90 days</strong>.</li>
        <li>We maintain a written schedule for the retention and destruction of biometric data.</li>
      </ul>
      <h3>3.5 Disclosure and Sale</h3>
      <ul>
        <li>We do <strong>not</strong> sell, lease, trade, or otherwise profit from biometric data.</li>
        <li>We do <strong>not</strong> disclose biometric data to third parties, except to service providers under contractual obligations.</li>
      </ul>
      <h3>3.6 Illinois Residents (BIPA)</h3>
      <p>
        If you are an Illinois resident, we comply with the Illinois Biometric Information Privacy
        Act (740 ILCS 14), including obtaining informed written consent, maintaining retention
        policies, and protecting biometric data with encryption at rest and in transit.
      </p>
      <h3>3.7 Texas and Washington Residents</h3>
      <p>We comply with applicable biometric data laws in Texas and Washington.</p>

      <hr />

      <h2>4. Data Sharing</h2>
      <h3>4.1 Service Providers</h3>
      <table>
        <thead><tr><th>Provider Category</th><th>Purpose</th><th>Data Shared</th></tr></thead>
        <tbody>
          <tr><td><strong>Cloud Infrastructure</strong> (Supabase)</td><td>Data storage, authentication</td><td>Account data, usage data, user content</td></tr>
          <tr><td><strong>Video Processing</strong> (Cloudflare)</td><td>Video storage and delivery</td><td>Uploaded videos</td></tr>
          <tr><td><strong>AI Model Providers</strong> (currently including OpenAI and Google Cloud Vision; providers may change with updated disclosure)</td><td>Video analysis, swing assessment</td><td>Video data, movement data (processed under DPAs)</td></tr>
          <tr><td><strong>Payment Processors</strong> (Apple, Google, Stripe)</td><td>Subscription billing</td><td>Payment and transaction data</td></tr>
          <tr><td><strong>Analytics</strong></td><td>Product usage analytics</td><td>Anonymized usage data</td></tr>
          <tr><td><strong>Communication</strong></td><td>Transactional email, support</td><td>Email address, support content</td></tr>
        </tbody>
      </table>

      <hr />

      <h2>5. Payment Information</h2>
      <p>
        If you subscribe through Apple, Google, or Stripe, they process your payment. We do not
        receive or store your full payment card information.
      </p>

      <hr />

      <h2>6. Data Retention</h2>
      <table>
        <thead><tr><th>Data Type</th><th>Retention Period</th></tr></thead>
        <tbody>
          <tr><td>Account Information</td><td>Duration of account + 30 days</td></tr>
          <tr><td>User Content (Videos)</td><td>Duration of account, deleted within 90 days</td></tr>
          <tr><td>Biometric Data</td><td>Max 3 years or until account deletion</td></tr>
          <tr><td>Usage and Log Data</td><td>24 months from collection</td></tr>
          <tr><td>Payment Records</td><td>As required by financial regulations (typically 7 years)</td></tr>
          <tr><td>Consent Records</td><td>Duration of account + 6 years, or the applicable statute of limitations (whichever is longer)</td></tr>
          <tr><td>Anonymized/Aggregated Data</td><td>Indefinitely (cannot identify you)</td></tr>
        </tbody>
      </table>

      <hr />

      <h2>7. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access</strong> — Request a copy of personal information we hold about you.</li>
        <li><strong>Correction</strong> — Request correction of inaccurate information.</li>
        <li><strong>Deletion</strong> — Request deletion of your account and data.</li>
        <li><strong>Withdraw Consent</strong> — Withdraw biometric data processing consent.</li>
        <li><strong>Data Portability</strong> — Request your data in a machine-readable format.</li>
      </ul>
      <h3>7.2 California Residents (CCPA/CPRA)</h3>
      <p>
        California residents have additional rights including the right to know, right to delete,
        right to opt-out of sale (we do not sell personal information), right to non-discrimination,
        and rights regarding sensitive personal information.
      </p>
      <h3>7.3 Canadian Residents (PIPEDA)</h3>
      <p>
        Canadian residents have rights under PIPEDA including access, correction, and the ability
        to withdraw consent for collection, use, or disclosure of personal information.
      </p>

      <hr />

      <h2>8. Children's Privacy</h2>
      <p>
        The Service is not intended for users under 13. Users between 13 and 17 may use the Service
        only with verified parental consent. We do not use minor user data for advertising. See the{' '}
        <Link to="/terms/parental-consent">Parental Consent Addendum</Link> for full details.
      </p>

      <hr />

      <h2>9. Data Security</h2>
      <p>
        We implement reasonable administrative, technical, and physical safeguards including
        encryption at rest and in transit, access controls, and regular security assessments.
      </p>

      <hr />

      <h2>10. Data Breach Notification</h2>
      <p>In the event of a data breach that compromises your personal information:</p>
      <ul>
        <li>We will notify affected users without unreasonable delay and no later than required by applicable law (e.g., within 60 days under most state breach notification laws; within 72 hours if required by applicable Canadian provincial law).</li>
        <li>We will notify applicable regulatory authorities as required by state and federal breach notification laws.</li>
        <li>Notification will include: a description of the breach, the types of information involved, steps we are taking, and steps you can take to protect yourself.</li>
      </ul>

      <hr />

      <h2>11. Cookies and Tracking Technologies</h2>
      <h3>11.1 What We Use</h3>
      <table>
        <thead><tr><th>Technology</th><th>Purpose</th><th>Duration</th></tr></thead>
        <tbody>
          <tr><td><strong>Session cookies</strong></td><td>Maintain your authenticated session</td><td>Expire when you close your browser or after inactivity</td></tr>
          <tr><td><strong>Authentication tokens</strong></td><td>Keep you logged in across visits</td><td>Until you log out or the token expires</td></tr>
          <tr><td><strong>Analytics</strong></td><td>Anonymized usage analytics</td><td>Session-based</td></tr>
          <tr><td><strong>Stripe</strong></td><td>Payment processing on web subscriptions</td><td>As required by Stripe for PCI compliance</td></tr>
        </tbody>
      </table>
      <h3>11.2 What We Do NOT Use</h3>
      <ul>
        <li>We do <strong>not</strong> use tracking pixels or advertising cookies.</li>
        <li>We do <strong>not</strong> use cross-site tracking.</li>
        <li>We do <strong>not</strong> share cookie data with advertising networks.</li>
        <li>We do <strong>not</strong> use cookies for behavioral advertising.</li>
      </ul>
      <h3>11.3 Your Choices</h3>
      <p>
        Most browsers allow you to control cookies through settings. Disabling cookies may affect
        the functionality of the Service, including your ability to remain logged in.
      </p>

      <hr />

      <h2>12. International Data</h2>
      <h3>12.1 Geographic Availability</h3>
      <p>
        The Service is currently available to users in the United States and Canada. The Service is
        not directed to users in the European Union, European Economic Area, or United Kingdom.
      </p>
      <h3>12.2 Data Location</h3>
      <p>
        Your data is processed and stored in the United States using infrastructure provided by
        Supabase and Cloudflare.
      </p>
      <h3>12.3 Canadian Users</h3>
      <p>
        If you are a Canadian resident, your data may be processed in the United States. We take
        reasonable steps to ensure your data is protected in accordance with PIPEDA and applicable
        provincial privacy laws.
      </p>

      <hr />

      <h2>13. Third-Party Links</h2>
      <p>
        The Service may contain links to third-party websites or services. We are not responsible
        for the privacy practices of third parties.
      </p>

      <hr />

      <h2>14. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated
        through the Service. For material changes affecting biometric data processing, we will
        obtain your renewed consent.
      </p>

      <hr />

      <h2>15. Contact Us</h2>
      <p>No Doubles Golf Co.</p>
      <p>Email: support@nodoublesgolf.com</p>
      <p>For California residents: Submit a CCPA/CPRA request with the subject line "Privacy Rights Request."</p>
      <p>For biometric data inquiries: Use the subject line "Biometric Data Inquiry."</p>

      <hr />

      <h2>16. Privacy Policy Summary</h2>
      <table>
        <thead><tr><th>What We Collect</th><th>Why</th><th>Shared With</th><th>Your Control</th></tr></thead>
        <tbody>
          <tr><td>Account info</td><td>Run your account</td><td>Service providers</td><td>Delete account anytime</td></tr>
          <tr><td>Golf profile</td><td>Personalize practice</td><td>Not shared externally</td><td>Edit in settings</td></tr>
          <tr><td>Videos you upload</td><td>Swing analysis</td><td>AI providers (processing only)</td><td>Delete anytime</td></tr>
          <tr><td>Movement data</td><td>Generate feedback</td><td>AI providers (processing only)</td><td>Withdraw consent</td></tr>
          <tr><td>Biometric data</td><td>Swing analysis</td><td>Not shared (processed internally and by AI providers under DPA)</td><td>Withdraw consent; deleted within 90 days</td></tr>
          <tr><td>Device and usage data</td><td>Product improvement</td><td>Analytics (anonymized only)</td><td>Delete account</td></tr>
          <tr><td>Payment status</td><td>Manage subscription</td><td>Apple/Google/Stripe</td><td>Cancel subscription</td></tr>
          <tr><td>Parent info (for minors)</td><td>Verify consent</td><td>Not shared</td><td>Parent can revoke</td></tr>
        </tbody>
      </table>
    </LegalPageLayout>
  );
}
